import React, { useEffect, useState } from 'react';
import { 
  Box,
  Button,
  Card,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { authApiClient } from '../../authApiClient';
import { setCachedProfileDisplay } from '../../services/profileDisplayCache';
import { userApiClient } from '../../userApiClient';
import { hostApiClient } from '../../hostApiClient';
import { UserProfile } from '../../models/Profile';
import { Organization, OrganizationType } from '../../models/Organization';
import SEO from '../../components/SEO';
import { authService } from '../../services/AuthService';
import { NotificationSnackbar, SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import { ProfilePhotoPicker } from '../../components/ProfilePhotoPicker';
import CurrentSubscriptionCard from '../../components/subscription/CurrentSubscriptionCard';
import { useSubscriptionFeaturesEnabled } from '../../utils/subscriptionFeatures';
import {
  cacheOrganizationType,
  defaultOrganizationNameFromUsername,
} from '../../utils/subscriptionOnboarding';
import { notifyOrganizationTypeUpdated } from '../../utils/subscriptionStripeReturn';
import ProviderOrganizationTypeToggle, {
  type ProviderOrganizationType,
} from '../../components/ProviderOrganizationTypeToggle';
import { isNetworkError } from '../../utils/errorHandler';
import { OrganizationSubscriptionStatus } from '../../models/Subscription';

const PROVIDER_UPGRADE_PROMPT_DISMISS_KEY = 'profile-become-provider-prompt-dismissed';

function isProviderUpgradePromptDismissed(): boolean {
  try {
    return localStorage.getItem(PROVIDER_UPGRADE_PROMPT_DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

function dismissProviderUpgradePrompt(): void {
  try {
    localStorage.setItem(PROVIDER_UPGRADE_PROMPT_DISMISS_KEY, 'true');
  } catch {
    // ignore quota / private mode failures
  }
}

const MyProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const subscriptionFeaturesEnabled = useSubscriptionFeaturesEnabled();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [name, setName] = useState('');
  const [bandName, setBandName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  /** Server value after last load or successful save; used to send `profileImageUrl: null` only when the user clears the photo. */
  const [initialProfileImageUrl, setInitialProfileImageUrl] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscriptionStatus | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [canDeleteOrg, setCanDeleteOrg] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [providerOrganizationType, setProviderOrganizationType] =
    useState<ProviderOrganizationType | null>(null);
  const [providerUpgradeError, setProviderUpgradeError] = useState<string | null>(null);
  const [providerUpgrading, setProviderUpgrading] = useState(false);
  const [providerUpgradePromptDismissed, setProviderUpgradePromptDismissed] = useState(
    isProviderUpgradePromptDismissed,
  );
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const userProfile = await userApiClient.getProfile();
        setProfile(userProfile);
        setName(userProfile.name);
        setBandName(userProfile.bandName || '');
        const img = userProfile.profileImageUrl || '';
        setProfileImageUrl(img);
        setInitialProfileImageUrl(img);

        // Check if user is a host
        const hasHostRole = userProfile.roles.includes('HOST');
        setIsHost(hasHostRole);
        
        // If user is a host, fetch organization details (and plan when subscriptions are enabled)
        if (hasHostRole) {
          try {
            const orgPromise = hostApiClient.getOrganization();
            const subscriptionPromise = subscriptionFeaturesEnabled
              ? hostApiClient.getSubscription().catch((subErr) => {
                  console.error('Failed to fetch subscription for profile:', subErr);
                  return null;
                })
              : Promise.resolve(null);
            const [org, status] = await Promise.all([orgPromise, subscriptionPromise]);
            setOrganization(org);
            setSubscription(status);

            // Check if user can delete organization (only member)
            const currentUserPhone = userProfile.phoneNumber;
            const otherMembers = org.members.filter(member => member.phoneNumber !== currentUserPhone);
            setCanDeleteOrg(otherMembers.length === 0);
          } catch (orgErr) {
            console.error('Failed to fetch organization:', orgErr);
            // If we can't fetch org, assume they can't delete it
            setCanDeleteOrg(false);
            setSubscription(null);
          }
        } else {
          setSubscription(null);
        }
      } catch (err) {
        setError(t('profile.fetchError', 'Failed to fetch profile'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // Intentionally once on mount; `t` from the test i18n mock (and often i18next) is not stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameChange = (value: string) => {
    setName(value);
    setHasChanges(true);
  };

  const handleBandNameChange = (value: string) => {
    setBandName(value);
    setHasChanges(true);
  };

  const handleCancel = () => {
    if (profile) {
      setName(profile.name);
      setBandName(profile.bandName || '');
      const img = profile.profileImageUrl || '';
      setProfileImageUrl(img);
      setInitialProfileImageUrl(img);
    }
    setHasChanges(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSave = async () => {
    try {
      const imageDirty = profileImageUrl.trim() !== initialProfileImageUrl.trim();
      await userApiClient.updateProfile({
        name,
        bandName,
        // Send "" to clear; omit when unchanged so the API does not wipe the photo.
        ...(imageDirty ? { profileImageUrl: profileImageUrl.trim() } : {}),
      });
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              name,
              bandName,
              ...(imageDirty
                ? { profileImageUrl: profileImageUrl.trim() ? profileImageUrl.trim() : undefined }
                : {}),
            }
          : null
      );
      if (imageDirty) {
        setInitialProfileImageUrl(profileImageUrl.trim());
      }
      setCachedProfileDisplay({
        name,
        profileImageUrl: profileImageUrl.trim() || null,
      });
      window.dispatchEvent(new Event('profile-updated'));
      setHasChanges(false);
      setSnackbarMessage(t('common.changesSaved', 'Changes saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setError(t('profile.updateError', 'Failed to update profile'));
      setSnackbarMessage(t('common.saveError', 'Failed to save changes'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // Set withOrg based on whether user can delete organization
      const shouldDeleteOrg = isHost && canDeleteOrg;
      await userApiClient.deleteProfile(shouldDeleteOrg);
      authService.logout();
      navigate('/');
    } catch (err) {
      setError(t('profile.deleteError', 'Failed to delete account'));
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const orgTypeLabel = (type: ProviderOrganizationType) =>
    t(`subscription.orgTypes.${type}`, type.replace(/_/g, ' '));

  const handleProviderUpgrade = async () => {
    setProviderUpgradeError(null);
    const accessToken = authService.getAccessToken();
    const displayName = (name.trim() || profile?.name || '').trim();

    if (!accessToken) {
      setProviderUpgradeError(
        t('authOnboarding.errors.missingToken', 'Authentication expired. Please sign in again.'),
      );
      return;
    }
    if (!displayName) {
      setProviderUpgradeError(
        t('authOnboarding.errors.usernameRequired', 'Username is required.'),
      );
      return;
    }
    if (!providerOrganizationType) {
      setProviderUpgradeError(
        t(
          'authOnboarding.errors.organizationTypeRequired',
          'Select music school, rehearsal studio, or individual tutor.',
        ),
      );
      return;
    }

    setProviderUpgrading(true);
    try {
      if (providerOrganizationType === OrganizationType.INDIVIDUAL_TUTOR) {
        await authApiClient.completeTutorProfile({
          accessToken,
          name: displayName,
        });
      } else {
        await authApiClient.completeHostProfile({
          accessToken,
          name: displayName,
          organizationName: defaultOrganizationNameFromUsername(displayName),
          organizationType: providerOrganizationType,
        });
      }

      const refreshed = await authService.refreshSessionAfterProfileCompletion();
      if (!refreshed) {
        setProviderUpgradeError(
          t(
            'authOnboarding.errors.sessionRefreshFailed',
            'Could not update your session. Please sign in again.',
          ),
        );
        return;
      }

      const roles = authService.getRoles();
      const expectedRole =
        providerOrganizationType === OrganizationType.INDIVIDUAL_TUTOR ? 'TUTOR' : 'HOST';
      if (!roles.includes(expectedRole) && !roles.includes('HOST')) {
        setProviderUpgradeError(
          t(
            'authOnboarding.errors.roleNotConfirmed',
            'Could not confirm host access. Please sign in again.',
          ),
        );
        return;
      }

      cacheOrganizationType(providerOrganizationType);
      notifyOrganizationTypeUpdated(providerOrganizationType);
      navigate(
        providerOrganizationType === OrganizationType.INDIVIDUAL_TUTOR
          ? '/tutor/listing'
          : '/host/organization',
        { replace: true },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setProviderUpgradeError(
        isNetworkError(message) ? t('auth.networkError') : message || t('auth.unknownError'),
      );
    } finally {
      setProviderUpgrading(false);
    }
  };

  const showProviderUpgradePrompt =
    Boolean(profile) && !isHost && !providerUpgradePromptDismissed;

  const handleDismissProviderUpgradePrompt = () => {
    dismissProviderUpgradePrompt();
    setProviderUpgradePromptDismissed(true);
  };
  const hasActivatedSubscription = Boolean(subscription?.status && subscription?.planCode);
  const showCurrentPlan =
    subscriptionFeaturesEnabled && isHost && hasActivatedSubscription && subscription != null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SEO 
        title={t('profile.title')}
        noindex={true}
      />
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto'}}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('profile.title', 'My Profile')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }}/>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', md: showCurrentPlan ? 'row' : 'column' }}
            spacing={2.5}
            alignItems={{ xs: 'stretch', md: showCurrentPlan ? 'flex-start' : 'stretch' }}
            sx={{ mb: showCurrentPlan ? { xs: 2, md: 3 } : 0 }}
          >
            {showCurrentPlan && (
              <Box
                sx={{
                  flex: { md: '0 1 360px' },
                  maxWidth: { md: 360 },
                  width: '100%',
                  minWidth: 0,
                  alignSelf: { md: 'flex-start' },
                  order: { xs: 2, md: 0 },
                }}
              >
                <CurrentSubscriptionCard subscription={subscription} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
                  <Button
                    component={RouterLink}
                    to="/host/subscription"
                    size="small"
                  >
                    {t('subscription.pageTitle', 'Plan and Billing')}
                  </Button>
                </Box>
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <ProfilePhotoPicker
                value={profileImageUrl || undefined}
                layout="centered"
                onUrlChange={(url) => {
                  setProfileImageUrl(url ?? '');
                  setHasChanges(true);
                }}
                onError={(message) => {
                  setSnackbarMessage(message);
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }}
              />

              {showCurrentPlan && (
                <>
                  <TextField
                    label={t('profile.name', 'Name')}
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    fullWidth
                    sx={{ mb: { xs: 2, md: 3 } }}
                  />

                  <TextField
                    label={t('profile.phoneNumber', 'Phone Number')}
                    value={profile?.phoneNumber || ''}
                    fullWidth
                    disabled
                    sx={{ mb: { xs: 2, md: 3 } }}
                  />

                  <TextField
                    label={t('profile.bandName', 'Band Name')}
                    value={bandName}
                    onChange={(e) => handleBandNameChange(e.target.value)}
                    fullWidth
                    sx={{ mb: 0 }}
                  />
                </>
              )}
            </Box>
          </Stack>

          {showProviderUpgradePrompt && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                position: 'relative',
                mb: { xs: 2, md: 3 },
                borderRadius: 2,
                alignItems: 'flex-start',
                px: { xs: 2, sm: 2.5 },
                py: 2,
                pr: 5,
                '& .MuiAlert-message': {
                  width: '100%',
                  minWidth: 0,
                  overflow: 'visible',
                  p: 0,
                },
              }}
              action={
                subscriptionFeaturesEnabled ? (
                  <Button
                    component={RouterLink}
                    to="/pricing"
                    color="inherit"
                    size="small"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('profile.becomeProvider.viewPlans', 'View our plans')}
                  </Button>
                ) : undefined
              }
            >
              <IconButton
                size="small"
                aria-label={t('profile.becomeProvider.dismiss', 'Dismiss')}
                onClick={handleDismissProviderUpgradePrompt}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  p: 0.5,
                  color: 'inherit',
                  opacity: 0.65,
                  '&:hover': { opacity: 1 },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('profile.becomeProvider.title', 'Do you host or teach?')}
              </Typography>
              {subscriptionFeaturesEnabled ? (
                <Typography variant="body2">
                  {t(
                    'profile.becomeProvider.pricingDescription',
                    'Start a free trial for rehearsal studios, music schools, and tutors.',
                  )}
                </Typography>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {t(
                      'profile.becomeProvider.selectTypeDescription',
                      'Choose how you want to use Band Space as a provider.',
                    )}
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <ProviderOrganizationTypeToggle
                      value={providerOrganizationType}
                      onChange={(value) => {
                        setProviderOrganizationType(value);
                        setProviderUpgradeError(null);
                      }}
                      labels={{
                        [OrganizationType.MUSIC_SCHOOL]: orgTypeLabel(OrganizationType.MUSIC_SCHOOL),
                        [OrganizationType.REHEARSAL_STUDIO]: orgTypeLabel(OrganizationType.REHEARSAL_STUDIO),
                        [OrganizationType.INDIVIDUAL_TUTOR]: orgTypeLabel(OrganizationType.INDIVIDUAL_TUTOR),
                      }}
                      ariaLabel={t('subscription.organizationTypeLabel', 'Organization type')}
                      disabled={providerUpgrading}
                    />
                  </Box>
                  {providerUpgradeError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {providerUpgradeError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleProviderUpgrade}
                    disabled={providerUpgrading || !providerOrganizationType}
                  >
                    {providerUpgrading
                      ? t('common.loading', 'Loading...')
                      : t('common.continue', 'Continue')}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          {!showCurrentPlan && (
            <>
              <TextField
                label={t('profile.name', 'Name')}
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                fullWidth
                sx={{ mb: { xs: 2, md: 3 } }}
              />

              <TextField
                label={t('profile.phoneNumber', 'Phone Number')}
                value={profile?.phoneNumber || ''}
                fullWidth
                disabled
                sx={{ mb: { xs: 2, md: 3 } }}
              />

              <TextField
                label={t('profile.bandName', 'Band Name')}
                value={bandName}
                onChange={(e) => handleBandNameChange(e.target.value)}
                fullWidth
                sx={{ mb: { xs: 2, md: 3 } }}
              />
            </>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            {/* Left side: SAVE and CANCEL buttons */}
            <Box display="flex" gap={{ xs: 1, sm: 2 }}>
              <Button 
                variant="contained" 
                onClick={handleSave} 
                disabled={!hasChanges}
                size="small"
                sx={{ 
                  minHeight: { xs: '32px', sm: '36px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500,
                  px: { xs: 1.5, sm: 2 }
                }}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleCancel} 
                disabled={!hasChanges}
                size="small"
                sx={{ 
                  minHeight: { xs: '32px', sm: '36px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1.5, sm: 2 }
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
            
            {/* Right side: DELETE ACCOUNT button */}
            <Button 
              variant="outlined" 
              color="error" 
              onClick={() => setDeleteDialogOpen(true)}
              size="small"
              sx={{ 
                minHeight: { xs: '32px', sm: '36px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.12)',
                  borderColor: '#c62828',
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                {t('profile.deleteAccount', 'Delete Account')}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                {t('profile.delete', 'Delete')}
              </Box>
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('profile.deleteAccountConfirm', 'Delete Account')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('profile.deleteAccountMessage', 'Are you sure you want to delete your account? This action cannot be undone.')}
          </Typography>
          
          {isHost && canDeleteOrg && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('profile.orgWillBeDeletedWarning', 'Your organization will also be deleted as you are the only member.')}
            </Alert>
          )}
          
          {isHost && !canDeleteOrg && organization && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('profile.orgNotDeletedWarning', 'Your organization will not be deleted as there are other members in it.')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('common.deleting', 'Deleting...') : t('profile.deleteAccount', 'Delete Account')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default MyProfilePage; 